import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { useNavigate } from "react-router-dom";
import api from "../api";

// ══════════════════════════════════════════════════════════════
// CONSTANTS
// ══════════════════════════════════════════════════════════════

const PRIMARY = "#005E60";
const DARK = "#1a1a2e";

const CLASSIFICATION_SOCIETIES = [
  "DNV",
  "LR",
  "ABS",
  "BV",
  "RINA",
  "CCS",
  "NK",
  "NAVSEA",
];

const VESSEL_TYPES = [
  "Offshore Supply Vessel",
  "Cargo Ship",
  "Tanker",
  "Ferry",
  "Cruise Ship",
  "Naval Vessel",
  "Research Vessel",
  "Dredger",
  "Tug",
  "Other",
];

const COMPONENT_TYPES = ["VFD", "TRANS", "MOTOR", "GEN", "SWBDS", "UPS", "DBR"];

const DIMENSION_FIELDS = new Set([
  "max_height_mm",
  "max_width_mm",
  "max_depth_mm",
  "max_weight_kg",
  "floor_loading_kn_m2",
]);

const isDimField = (key) => DIMENSION_FIELDS.has(key);

const COMPONENT_SPECS = {
  VFD: [
    {
      key: "power_rating_kw",
      label: "Power Rating (kW)",
      type: "number",
      placeholder: "e.g. 4500",
      required: true,
    },
    {
      key: "input_voltage",
      label: "Input Voltage",
      type: "text",
      placeholder: "e.g. 6600V AC",
      required: true,
    },
    {
      key: "output_voltage",
      label: "Output Voltage",
      type: "text",
      placeholder: "e.g. 0 to 1850V AC",
      required: true,
    },
    {
      key: "input_frequency",
      label: "Input Frequency",
      type: "text",
      placeholder: "e.g. 60Hz",
      required: true,
    },
    {
      key: "output_frequency",
      label: "Output Frequency",
      type: "text",
      placeholder: "e.g. 0 to 75Hz variable",
      required: true,
    },
    {
      key: "dc_bus_voltage",
      label: "DC Bus Voltage",
      type: "text",
      placeholder: "e.g. 1100V DC",
      required: false,
    },
    {
      key: "input_current_a",
      label: "Input Current (A)",
      type: "number",
      placeholder: "e.g. 800",
      required: false,
    },
    {
      key: "output_current_a",
      label: "Output Current (A)",
      type: "number",
      placeholder: "e.g. 800",
      required: false,
    },
    {
      key: "switching_frequency_khz",
      label: "Switching Frequency (kHz)",
      type: "number",
      placeholder: "e.g. 4",
      required: false,
    },
    {
      key: "efficiency_percent",
      label: "Efficiency (%)",
      type: "number",
      placeholder: "e.g. 98.5",
      required: false,
    },
    {
      key: "power_factor",
      label: "Power Factor",
      type: "text",
      placeholder: "e.g. 0.95 at full load",
      required: false,
    },
    {
      key: "harmonic_filter",
      label: "Harmonic Filter",
      type: "text",
      placeholder: "e.g. Active front end — AFE rectifier",
      required: false,
    },
    {
      key: "harmonic_distortion_thd",
      label: "Harmonic Distortion (THD)",
      type: "text",
      placeholder: "e.g. 5% max at PCC",
      required: false,
    },
    {
      key: "braking_method",
      label: "Braking Method",
      type: "text",
      placeholder: "e.g. Dynamic braking resistor",
      required: false,
    },
    {
      key: "protection_functions",
      label: "Protection Functions",
      type: "text",
      placeholder: "e.g. Overcurrent, overvoltage, earth fault",
      required: false,
    },
    {
      key: "communication_protocol",
      label: "Communication Protocol",
      type: "text",
      placeholder: "e.g. Modbus TCP/IP via ship IPMS",
      required: false,
    },
    {
      key: "cooling_method",
      label: "Cooling Method",
      type: "text",
      placeholder: "e.g. Forced air — internal fans",
      required: false,
    },
    {
      key: "enclosure_rating",
      label: "Enclosure Rating (IP)",
      type: "text",
      placeholder: "e.g. IP54",
      required: true,
    },
    {
      key: "insulation_class",
      label: "Insulation Class",
      type: "text",
      placeholder: "e.g. F",
      required: false,
    },
    {
      key: "overload_capacity",
      label: "Overload Capacity",
      type: "text",
      placeholder: "e.g. 150% for 60 seconds",
      required: false,
    },
    {
      key: "mounting_arrangement",
      label: "Mounting Arrangement",
      type: "text",
      placeholder: "e.g. Free-standing floor mounted",
      required: false,
    },
    {
      key: "shock_standard",
      label: "Shock Standard",
      type: "text",
      placeholder: "e.g. MIL-S-901D Grade A",
      required: false,
    },
    {
      key: "emc_standard",
      label: "EMC Standard",
      type: "text",
      placeholder: "e.g. MIL-STD-461G",
      required: false,
    },
    {
      key: "max_height_mm",
      label: "Max Height — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_width_mm",
      label: "Max Width — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_depth_mm",
      label: "Max Depth — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_weight_kg",
      label: "Max Weight — Do Not Exceed (kg)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "floor_loading_kn_m2",
      label: "Max Floor Loading — Do Not Exceed (kN/m²)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "special_requirements",
      label: "Special Requirements",
      type: "multiline",
      placeholder: "e.g. All alarms hardwired to IPMS. FAT witnessed by ABS.",
      required: false,
    },
  ],
  TRANS: [
    {
      key: "power_rating_kva",
      label: "Power Rating (kVA)",
      type: "number",
      placeholder: "e.g. 5400",
      required: true,
    },
    {
      key: "primary_voltage",
      label: "Primary Voltage",
      type: "text",
      placeholder: "e.g. 6600V",
      required: true,
    },
    {
      key: "secondary_voltage",
      label: "Secondary Voltage",
      type: "text",
      placeholder: "e.g. 1850V",
      required: true,
    },
    {
      key: "frequency",
      label: "Frequency",
      type: "text",
      placeholder: "e.g. 60Hz",
      required: true,
    },
    {
      key: "vector_group",
      label: "Vector Group",
      type: "text",
      placeholder: "e.g. Dyn11",
      required: true,
    },
    {
      key: "impedance_voltage_percent",
      label: "Impedance Voltage (%)",
      type: "number",
      placeholder: "e.g. 6.0",
      required: true,
    },
    {
      key: "no_load_losses_w",
      label: "No-Load Losses (W)",
      type: "number",
      placeholder: "e.g. 5500",
      required: false,
    },
    {
      key: "full_load_losses_w",
      label: "Full Load Losses (W)",
      type: "number",
      placeholder: "e.g. 42000",
      required: false,
    },
    {
      key: "no_load_current_percent",
      label: "No-Load Current (%)",
      type: "number",
      placeholder: "e.g. 1.5",
      required: false,
    },
    {
      key: "winding_configuration",
      label: "Winding Configuration",
      type: "text",
      placeholder: "e.g. Cast resin — Class F",
      required: false,
    },
    {
      key: "insulation_class",
      label: "Insulation Class",
      type: "text",
      placeholder: "e.g. F",
      required: true,
    },
    {
      key: "temperature_rise_k",
      label: "Temperature Rise (K)",
      type: "number",
      placeholder: "e.g. 100",
      required: false,
    },
    {
      key: "cooling_type",
      label: "Cooling Type",
      type: "text",
      placeholder: "e.g. AN natural air",
      required: true,
    },
    {
      key: "enclosure_rating",
      label: "Enclosure Rating (IP)",
      type: "text",
      placeholder: "e.g. IP44",
      required: true,
    },
    {
      key: "tapping_range",
      label: "Tapping Range",
      type: "text",
      placeholder: "e.g. +/-5% in 2.5% steps",
      required: false,
    },
    {
      key: "turns_ratio",
      label: "Turns Ratio",
      type: "text",
      placeholder: "e.g. 9.565:1",
      required: false,
    },
    {
      key: "primary_current_a",
      label: "Primary Current (A)",
      type: "number",
      placeholder: "e.g. 481",
      required: false,
    },
    {
      key: "secondary_current_a",
      label: "Secondary Current (A)",
      type: "number",
      placeholder: "e.g. 4602",
      required: false,
    },
    {
      key: "fault_current_contribution_ka",
      label: "Fault Current Contribution (kA)",
      type: "number",
      placeholder: "e.g. 15.75",
      required: false,
    },
    {
      key: "short_circuit_withstand_ka",
      label: "Short Circuit Withstand (kA)",
      type: "number",
      placeholder: "e.g. 15.75",
      required: false,
    },
    {
      key: "short_circuit_duration_s",
      label: "Short Circuit Duration (s)",
      type: "number",
      placeholder: "e.g. 2",
      required: false,
    },
    {
      key: "partial_discharge_pc",
      label: "Partial Discharge (pC)",
      type: "text",
      placeholder: "e.g. 10pC max",
      required: false,
    },
    {
      key: "shock_standard",
      label: "Shock Standard",
      type: "text",
      placeholder: "e.g. MIL-S-901D Grade A",
      required: false,
    },
    {
      key: "emc_standard",
      label: "EMC Standard",
      type: "text",
      placeholder: "e.g. IEC 61000-2-4 Class 3",
      required: false,
    },
    {
      key: "max_height_mm",
      label: "Max Height — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_width_mm",
      label: "Max Width — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_depth_mm",
      label: "Max Depth — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_weight_kg",
      label: "Max Weight — Do Not Exceed (kg)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "floor_loading_kn_m2",
      label: "Max Floor Loading — Do Not Exceed (kN/m²)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "special_requirements",
      label: "Special Requirements",
      type: "multiline",
      placeholder: "e.g. Cast resin VPI. Type test certs per IEC 60076-11.",
      required: false,
    },
  ],
  MOTOR: [
    {
      key: "rated_power_kw",
      label: "Rated Power (kW)",
      type: "number",
      placeholder: "e.g. 4200",
      required: true,
    },
    {
      key: "rated_voltage",
      label: "Rated Voltage",
      type: "text",
      placeholder: "e.g. 1850V",
      required: true,
    },
    {
      key: "frequency",
      label: "Frequency",
      type: "text",
      placeholder: "e.g. 75Hz",
      required: true,
    },
    {
      key: "rated_speed_rpm",
      label: "Rated Speed (RPM)",
      type: "number",
      placeholder: "e.g. 150",
      required: true,
    },
    {
      key: "synchronous_speed_rpm",
      label: "Synchronous Speed (RPM)",
      type: "number",
      placeholder: "e.g. 1800",
      required: false,
    },
    {
      key: "number_of_poles",
      label: "Number of Poles",
      type: "number",
      placeholder: "e.g. 4",
      required: false,
    },
    {
      key: "rated_current_a",
      label: "Rated Current (A)",
      type: "number",
      placeholder: "e.g. 1400",
      required: false,
    },
    {
      key: "starting_current_a",
      label: "Starting Current (A)",
      type: "number",
      placeholder: "e.g. 2800",
      required: false,
    },
    {
      key: "power_factor",
      label: "Power Factor",
      type: "text",
      placeholder: "e.g. 0.90",
      required: false,
    },
    {
      key: "efficiency_percent",
      label: "Efficiency (%)",
      type: "number",
      placeholder: "e.g. 93.5",
      required: false,
    },
    {
      key: "efficiency_class",
      label: "Efficiency Class",
      type: "text",
      placeholder: "e.g. IE3",
      required: false,
    },
    {
      key: "insulation_class",
      label: "Insulation Class",
      type: "text",
      placeholder: "e.g. Class H",
      required: true,
    },
    {
      key: "temperature_rise_k",
      label: "Temperature Rise (K)",
      type: "number",
      placeholder: "e.g. 155",
      required: false,
    },
    {
      key: "enclosure_rating",
      label: "Enclosure Rating (IP)",
      type: "text",
      placeholder: "e.g. IP44",
      required: true,
    },
    {
      key: "mounting_type",
      label: "Mounting Type",
      type: "text",
      placeholder: "e.g. B3F",
      required: false,
    },
    {
      key: "frame_size",
      label: "Frame Size",
      type: "text",
      placeholder: "e.g. IEC 500L",
      required: false,
    },
    {
      key: "bearing_type",
      label: "Bearing Type",
      type: "text",
      placeholder: "e.g. Anti-friction — insulated DE",
      required: false,
    },
    {
      key: "cooling_method",
      label: "Cooling Method",
      type: "text",
      placeholder: "e.g. IC411 TEFC",
      required: false,
    },
    {
      key: "starting_method",
      label: "Starting Method",
      type: "text",
      placeholder: "e.g. VFD",
      required: false,
    },
    {
      key: "service_factor",
      label: "Service Factor",
      type: "number",
      placeholder: "e.g. 1.0",
      required: false,
    },
    {
      key: "duty_cycle",
      label: "Duty Cycle",
      type: "text",
      placeholder: "e.g. S1 continuous",
      required: false,
    },
    {
      key: "locked_rotor_torque_percent",
      label: "Locked Rotor Torque (%)",
      type: "number",
      placeholder: "e.g. 150",
      required: false,
    },
    {
      key: "breakdown_torque_percent",
      label: "Breakdown Torque (%)",
      type: "number",
      placeholder: "e.g. 250",
      required: false,
    },
    {
      key: "moment_of_inertia_kgm2",
      label: "Moment of Inertia (kg·m²)",
      type: "number",
      placeholder: "e.g. 12.5",
      required: false,
    },
    {
      key: "shock_standard",
      label: "Shock Standard",
      type: "text",
      placeholder: "e.g. MIL-S-901D Grade A",
      required: false,
    },
    {
      key: "emc_standard",
      label: "EMC Standard",
      type: "text",
      placeholder: "e.g. IEC 61000-2-4 Class 3",
      required: false,
    },
    {
      key: "max_height_mm",
      label: "Max Height — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_width_mm",
      label: "Max Width — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_depth_mm",
      label: "Max Depth — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_weight_kg",
      label: "Max Weight — Do Not Exceed (kg)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "floor_loading_kn_m2",
      label: "Max Floor Loading — Do Not Exceed (kN/m²)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "special_requirements",
      label: "Special Requirements",
      type: "multiline",
      placeholder: "e.g. Space heaters 240V. Bearing insulation on DE.",
      required: false,
    },
  ],
  GEN: [
    {
      key: "rated_power_kva",
      label: "Rated Power (kVA)",
      type: "number",
      placeholder: "e.g. 3000",
      required: true,
    },
    {
      key: "rated_power_kw",
      label: "Rated Power (kW)",
      type: "number",
      placeholder: "e.g. 2400",
      required: true,
    },
    {
      key: "rated_voltage",
      label: "Rated Voltage",
      type: "text",
      placeholder: "e.g. 6600V",
      required: true,
    },
    {
      key: "frequency",
      label: "Frequency",
      type: "text",
      placeholder: "e.g. 60Hz",
      required: true,
    },
    {
      key: "power_factor",
      label: "Power Factor",
      type: "text",
      placeholder: "e.g. 0.8 lagging",
      required: true,
    },
    {
      key: "speed_rpm",
      label: "Speed (RPM)",
      type: "number",
      placeholder: "e.g. 1800",
      required: true,
    },
    {
      key: "number_of_poles",
      label: "Number of Poles",
      type: "number",
      placeholder: "e.g. 4",
      required: false,
    },
    {
      key: "rated_current_a",
      label: "Rated Current (A)",
      type: "number",
      placeholder: "e.g. 262",
      required: false,
    },
    {
      key: "efficiency_percent",
      label: "Efficiency (%)",
      type: "number",
      placeholder: "e.g. 96.5",
      required: false,
    },
    {
      key: "insulation_class",
      label: "Insulation Class",
      type: "text",
      placeholder: "e.g. Class H",
      required: true,
    },
    {
      key: "temperature_rise_k",
      label: "Temperature Rise (K)",
      type: "number",
      placeholder: "e.g. 105",
      required: false,
    },
    {
      key: "enclosure_rating",
      label: "Enclosure Rating (IP)",
      type: "text",
      placeholder: "e.g. IP23",
      required: true,
    },
    {
      key: "excitation_type",
      label: "Excitation Type",
      type: "text",
      placeholder: "e.g. Brushless PMG",
      required: false,
    },
    {
      key: "avr_type",
      label: "AVR Type",
      type: "text",
      placeholder: "e.g. Digital AVR — droop + isochronous",
      required: false,
    },
    {
      key: "voltage_regulation_percent",
      label: "Voltage Regulation (%)",
      type: "text",
      placeholder: "e.g. +/-0.5 steady state",
      required: false,
    },
    {
      key: "transient_voltage_dip_percent",
      label: "Transient Voltage Dip (%)",
      type: "text",
      placeholder: "e.g. 15 max on full load",
      required: false,
    },
    {
      key: "frequency_regulation_percent",
      label: "Frequency Regulation (%)",
      type: "text",
      placeholder: "e.g. +/-5 transient",
      required: false,
    },
    {
      key: "subtransient_reactance_xd",
      label: 'Subtransient Reactance X"d (pu)',
      type: "text",
      placeholder: "e.g. 0.12 pu",
      required: false,
    },
    {
      key: "transient_reactance_xd",
      label: "Transient Reactance X'd (pu)",
      type: "text",
      placeholder: "e.g. 0.20 pu",
      required: false,
    },
    {
      key: "synchronous_reactance_xd",
      label: "Synchronous Reactance Xd (pu)",
      type: "text",
      placeholder: "e.g. 1.8 pu",
      required: false,
    },
    {
      key: "short_circuit_ratio",
      label: "Short Circuit Ratio (SCR)",
      type: "number",
      placeholder: "e.g. 0.55",
      required: false,
    },
    {
      key: "fault_current_contribution_ka",
      label: "Fault Current Contribution",
      type: "text",
      placeholder: "e.g. 3x rated for 3s",
      required: false,
    },
    {
      key: "parallel_operation",
      label: "Parallel Operation",
      type: "text",
      placeholder: "e.g. Yes — droop mode",
      required: false,
    },
    {
      key: "cooling_method",
      label: "Cooling Method",
      type: "text",
      placeholder: "e.g. IC01 ODP",
      required: false,
    },
    {
      key: "mounting_arrangement",
      label: "Mounting Arrangement",
      type: "text",
      placeholder: "e.g. Foot mounted — flexibly coupled",
      required: false,
    },
    {
      key: "shock_standard",
      label: "Shock Standard",
      type: "text",
      placeholder: "e.g. MIL-S-901D Grade A",
      required: false,
    },
    {
      key: "emc_standard",
      label: "EMC Standard",
      type: "text",
      placeholder: "e.g. IEC 61000-2-4 Class 3",
      required: false,
    },
    {
      key: "max_height_mm",
      label: "Max Height — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_width_mm",
      label: "Max Width — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_depth_mm",
      label: "Max Depth — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_weight_kg",
      label: "Max Weight — Do Not Exceed (kg)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "floor_loading_kn_m2",
      label: "Max Floor Loading — Do Not Exceed (kN/m²)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "special_requirements",
      label: "Special Requirements",
      type: "multiline",
      placeholder:
        "e.g. Tropicalised. AVR PF controller mode. Harmonic spectrum report.",
      required: false,
    },
  ],
  SWBDS: [
    {
      key: "bus_voltage",
      label: "Bus Voltage",
      type: "text",
      placeholder: "e.g. 690V",
      required: true,
    },
    {
      key: "bus_current_rating_a",
      label: "Bus Current Rating (A)",
      type: "number",
      placeholder: "e.g. 5000",
      required: true,
    },
    {
      key: "frequency",
      label: "Frequency",
      type: "text",
      placeholder: "e.g. 60Hz",
      required: true,
    },
    {
      key: "fault_level_ka",
      label: "Fault Level (kA)",
      type: "number",
      placeholder: "e.g. 50",
      required: true,
    },
    {
      key: "fault_duration_s",
      label: "Fault Duration (s)",
      type: "number",
      placeholder: "e.g. 1",
      required: false,
    },
    {
      key: "number_of_incoming_feeders",
      label: "Number of Incoming Feeders",
      type: "number",
      placeholder: "e.g. 2",
      required: false,
    },
    {
      key: "incoming_breaker_rating_a",
      label: "Incoming Breaker Rating (A)",
      type: "number",
      placeholder: "e.g. 5000",
      required: false,
    },
    {
      key: "number_of_outgoing_feeders",
      label: "Number of Outgoing Feeders",
      type: "number",
      placeholder: "e.g. 32",
      required: false,
    },
    {
      key: "busbar_material",
      label: "Busbar Material",
      type: "text",
      placeholder: "e.g. Copper — tin plated",
      required: false,
    },
    {
      key: "busbar_rating_a",
      label: "Busbar Rating (A)",
      type: "number",
      placeholder: "e.g. 5000",
      required: false,
    },
    {
      key: "busbar_configuration",
      label: "Busbar Configuration",
      type: "text",
      placeholder: "e.g. Single busbar with bus-tie",
      required: false,
    },
    {
      key: "enclosure_rating",
      label: "Enclosure Rating (IP)",
      type: "text",
      placeholder: "e.g. IP42",
      required: true,
    },
    {
      key: "protection_standard",
      label: "Protection Standard",
      type: "text",
      placeholder: "e.g. IEC 61439-1/2",
      required: false,
    },
    {
      key: "form_of_separation",
      label: "Form of Separation",
      type: "text",
      placeholder: "e.g. Form 4 Type 7",
      required: false,
    },
    {
      key: "breaking_capacity_ka",
      label: "Breaking Capacity (kA)",
      type: "number",
      placeholder: "e.g. 65",
      required: false,
    },
    {
      key: "arc_flash_mitigation",
      label: "Arc Flash Mitigation",
      type: "text",
      placeholder: "e.g. ZSI + arc flash relays",
      required: false,
    },
    {
      key: "protection_relay_type",
      label: "Protection Relay Type",
      type: "text",
      placeholder: "e.g. Numerical — IEC 61850",
      required: false,
    },
    {
      key: "metering",
      label: "Metering",
      type: "text",
      placeholder: "e.g. Power analyser per feeder",
      required: false,
    },
    {
      key: "neutral_earthing",
      label: "Neutral Earthing",
      type: "text",
      placeholder: "e.g. High resistance earthed",
      required: false,
    },
    {
      key: "cooling_method",
      label: "Cooling Method",
      type: "text",
      placeholder: "e.g. Natural convection",
      required: false,
    },
    {
      key: "mounting_arrangement",
      label: "Mounting Arrangement",
      type: "text",
      placeholder: "e.g. Free-standing floor mounted",
      required: false,
    },
    {
      key: "shock_standard",
      label: "Shock Standard",
      type: "text",
      placeholder: "e.g. MIL-S-901D Grade A",
      required: false,
    },
    {
      key: "emc_standard",
      label: "EMC Standard",
      type: "text",
      placeholder: "e.g. MIL-STD-461G",
      required: false,
    },
    {
      key: "max_height_mm",
      label: "Max Height — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_width_mm",
      label: "Max Width — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_depth_mm",
      label: "Max Depth — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_weight_kg",
      label: "Max Weight — Do Not Exceed (kg)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "floor_loading_kn_m2",
      label: "Max Floor Loading — Do Not Exceed (kN/m²)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "special_requirements",
      label: "Special Requirements",
      type: "multiline",
      placeholder:
        "e.g. Fully type tested per IEC 61439. Arc flash study prior to FAT.",
      required: false,
    },
  ],
  UPS: [
    {
      key: "power_rating_kva",
      label: "Power Rating (kVA)",
      type: "number",
      placeholder: "e.g. 150",
      required: true,
    },
    {
      key: "power_rating_kw",
      label: "Power Rating (kW)",
      type: "number",
      placeholder: "e.g. 120",
      required: true,
    },
    {
      key: "input_voltage",
      label: "Input Voltage",
      type: "text",
      placeholder: "e.g. 440V",
      required: true,
    },
    {
      key: "output_voltage",
      label: "Output Voltage",
      type: "text",
      placeholder: "e.g. 440V",
      required: true,
    },
    {
      key: "frequency",
      label: "Frequency",
      type: "text",
      placeholder: "e.g. 60Hz",
      required: true,
    },
    {
      key: "input_frequency_range",
      label: "Input Frequency Range",
      type: "text",
      placeholder: "e.g. 57 to 63Hz",
      required: false,
    },
    {
      key: "output_frequency_regulation",
      label: "Output Frequency Regulation",
      type: "text",
      placeholder: "e.g. +/-0.1Hz",
      required: false,
    },
    {
      key: "output_voltage_regulation",
      label: "Output Voltage Regulation",
      type: "text",
      placeholder: "e.g. +/-1% steady state",
      required: false,
    },
    {
      key: "efficiency_percent",
      label: "Efficiency (%)",
      type: "number",
      placeholder: "e.g. 96",
      required: false,
    },
    {
      key: "topology",
      label: "Topology",
      type: "text",
      placeholder: "e.g. Online double conversion",
      required: true,
    },
    {
      key: "battery_type",
      label: "Battery Type",
      type: "text",
      placeholder: "e.g. VRLA AGM",
      required: true,
    },
    {
      key: "battery_autonomy_min",
      label: "Battery Autonomy (min)",
      type: "number",
      placeholder: "e.g. 30",
      required: true,
    },
    {
      key: "battery_voltage",
      label: "Battery Voltage (DC)",
      type: "text",
      placeholder: "e.g. 240V DC",
      required: false,
    },
    {
      key: "battery_recharge_time_h",
      label: "Battery Recharge Time (h)",
      type: "number",
      placeholder: "e.g. 8",
      required: false,
    },
    {
      key: "bypass_rating_a",
      label: "Bypass Rating (A)",
      type: "number",
      placeholder: "e.g. 200",
      required: false,
    },
    {
      key: "static_bypass",
      label: "Static Bypass",
      type: "text",
      placeholder: "e.g. Yes — auto no-break transfer",
      required: false,
    },
    {
      key: "maintenance_bypass",
      label: "Maintenance Bypass",
      type: "text",
      placeholder: "e.g. Yes — manual isolator",
      required: false,
    },
    {
      key: "overload_capacity",
      label: "Overload Capacity",
      type: "text",
      placeholder: "e.g. 125% for 10 min",
      required: false,
    },
    {
      key: "harmonic_distortion_thd",
      label: "Output THD",
      type: "text",
      placeholder: "e.g. 3% max linear load",
      required: false,
    },
    {
      key: "power_factor_input",
      label: "Input Power Factor",
      type: "text",
      placeholder: "e.g. 0.99 with PFC",
      required: false,
    },
    {
      key: "power_factor_output",
      label: "Output Power Factor",
      type: "text",
      placeholder: "e.g. 0.8 to unity",
      required: false,
    },
    {
      key: "enclosure_rating",
      label: "Enclosure Rating (IP)",
      type: "text",
      placeholder: "e.g. IP42",
      required: true,
    },
    {
      key: "insulation_class",
      label: "Insulation Class",
      type: "text",
      placeholder: "e.g. F",
      required: false,
    },
    {
      key: "cooling_method",
      label: "Cooling Method",
      type: "text",
      placeholder: "e.g. Forced air with filter",
      required: false,
    },
    {
      key: "mounting_arrangement",
      label: "Mounting Arrangement",
      type: "text",
      placeholder: "e.g. Free-standing floor mounted",
      required: false,
    },
    {
      key: "communication_protocol",
      label: "Communication Protocol",
      type: "text",
      placeholder: "e.g. SNMP v3 + Modbus TCP",
      required: false,
    },
    {
      key: "shock_standard",
      label: "Shock Standard",
      type: "text",
      placeholder: "e.g. MIL-S-901D Grade A",
      required: false,
    },
    {
      key: "emc_standard",
      label: "EMC Standard",
      type: "text",
      placeholder: "e.g. IEC 61000-2-4 Class 3",
      required: false,
    },
    {
      key: "max_height_mm",
      label: "Max Height — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_width_mm",
      label: "Max Width — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_depth_mm",
      label: "Max Depth — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_weight_kg",
      label: "Max Weight — Do Not Exceed (kg)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "floor_loading_kn_m2",
      label: "Max Floor Loading — Do Not Exceed (kN/m²)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "special_requirements",
      label: "Special Requirements",
      type: "multiline",
      placeholder: "e.g. SNMP card. Battery hot-swap. FAT witnessed by class.",
      required: false,
    },
  ],
  DBR: [
    {
      key: "dc_bus_voltage",
      label: "DC Bus Voltage",
      type: "text",
      placeholder: "e.g. 1100V DC",
      required: true,
    },
    {
      key: "peak_braking_power_kw",
      label: "Peak Braking Power (kW)",
      type: "number",
      placeholder: "e.g. 2500",
      required: true,
    },
    {
      key: "continuous_braking_power_kw",
      label: "Continuous Braking Power (kW)",
      type: "number",
      placeholder: "e.g. 500",
      required: true,
    },
    {
      key: "braking_duration_peak_s",
      label: "Peak Braking Duration (s)",
      type: "number",
      placeholder: "e.g. 60",
      required: false,
    },
    {
      key: "braking_duration_continuous",
      label: "Continuous Braking Duration",
      type: "text",
      placeholder: "e.g. Continuous at 500kW",
      required: false,
    },
    {
      key: "duty_cycle",
      label: "Duty Cycle",
      type: "text",
      placeholder: "e.g. 10% — 60s on / 540s off",
      required: false,
    },
    {
      key: "resistance_value_ohm",
      label: "Resistance Value (Ω)",
      type: "number",
      placeholder: "e.g. 0.48",
      required: false,
    },
    {
      key: "resistance_tolerance",
      label: "Resistance Tolerance",
      type: "text",
      placeholder: "e.g. +/-5%",
      required: false,
    },
    {
      key: "peak_current_a",
      label: "Peak Current (A)",
      type: "number",
      placeholder: "e.g. 2273",
      required: false,
    },
    {
      key: "continuous_current_a",
      label: "Continuous Current (A)",
      type: "number",
      placeholder: "e.g. 455",
      required: false,
    },
    {
      key: "energy_per_cycle_kwh",
      label: "Energy Per Cycle (kWh)",
      type: "number",
      placeholder: "e.g. 41.7",
      required: false,
    },
    {
      key: "insulation_class",
      label: "Insulation Class",
      type: "text",
      placeholder: "e.g. H",
      required: false,
    },
    {
      key: "thermal_trip_temperature_c",
      label: "Thermal Trip Temperature (°C)",
      type: "number",
      placeholder: "e.g. 180",
      required: false,
    },
    {
      key: "thermal_trip_reset",
      label: "Thermal Trip Reset",
      type: "text",
      placeholder: "e.g. Manual only",
      required: false,
    },
    {
      key: "temperature_monitoring",
      label: "Temperature Monitoring",
      type: "text",
      placeholder: "e.g. 2x Pt100 RTD — wired to VFD",
      required: false,
    },
    {
      key: "number_of_resistor_banks",
      label: "Number of Resistor Banks",
      type: "number",
      placeholder: "e.g. 2",
      required: false,
    },
    {
      key: "resistor_element_material",
      label: "Resistor Element Material",
      type: "text",
      placeholder: "e.g. Stainless steel — NOT nickel-chrome",
      required: false,
    },
    {
      key: "enclosure_rating",
      label: "Enclosure Rating (IP)",
      type: "text",
      placeholder: "e.g. IP54",
      required: true,
    },
    {
      key: "cooling_method",
      label: "Cooling Method",
      type: "text",
      placeholder: "e.g. Forced air for peak duty",
      required: false,
    },
    {
      key: "mounting_arrangement",
      label: "Mounting Arrangement",
      type: "text",
      placeholder: "e.g. Floor mounted — 100mm clearance",
      required: false,
    },
    {
      key: "connected_vfd_tag",
      label: "Connected VFD Tag",
      type: "text",
      placeholder: "e.g. TAG-VFD-001",
      required: false,
    },
    {
      key: "connected_motor_tag",
      label: "Connected Motor Tag",
      type: "text",
      placeholder: "e.g. TAG-MOTOR-001",
      required: false,
    },
    {
      key: "shock_standard",
      label: "Shock Standard",
      type: "text",
      placeholder: "e.g. MIL-S-901D Grade A",
      required: false,
    },
    {
      key: "emc_standard",
      label: "EMC Standard",
      type: "text",
      placeholder: "e.g. MIL-STD-461G",
      required: false,
    },
    {
      key: "max_height_mm",
      label: "Max Height — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_width_mm",
      label: "Max Width — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_depth_mm",
      label: "Max Depth — Do Not Exceed (mm)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "max_weight_kg",
      label: "Max Weight — Do Not Exceed (kg)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "floor_loading_kn_m2",
      label: "Max Floor Loading — Do Not Exceed (kN/m²)",
      type: "number",
      placeholder: "Leave blank if no restriction",
      required: false,
    },
    {
      key: "special_requirements",
      label: "Special Requirements",
      type: "multiline",
      placeholder: "e.g. Weatherproof hood. All terminals shrouded IP2X.",
      required: false,
    },
  ],
};

const COMPONENT_COLORS = {
  VFD: { bg: "#005E60", light: "#e6f4f4" },
  TRANS: { bg: "#007b7d", light: "#e6f5f5" },
  MOTOR: { bg: "#00843d", light: "#e6f5ec" },
  GEN: { bg: "#c47a00", light: "#fdf3e3" },
  SWBDS: { bg: "#5c35a0", light: "#f0eafa" },
  UPS: { bg: "#b00033", light: "#fce8ed" },
  DBR: { bg: "#0077b6", light: "#e6f2fb" },
};

// ══════════════════════════════════════════════════════════════
// STYLES
// ══════════════════════════════════════════════════════════════

const fieldStyle = {
  "& label.Mui-focused": { color: PRIMARY },
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    "&.Mui-focused fieldset": { borderColor: PRIMARY },
  },
};

const formatLabel = (key) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// ══════════════════════════════════════════════════════════════
// COMPONENT
// ══════════════════════════════════════════════════════════════

function ImportProject() {
  const navigate = useNavigate();
  const fileRef = useRef();

  const [step, setStep] = useState("upload");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fileName, setFileName] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [project, setProject] = useState(null);
  const [components, setComponents] = useState([]);
  const [editingComp, setEditingComp] = useState(null);

  // ── File upload ───────────────────────────────────────────
  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith(".pdf") && !name.endsWith(".docx")) {
      setError("Only PDF and Word (.docx) files are supported.");
      return;
    }
    setFileName(file.name);
    setUploading(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post("/import/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { extracted } = res.data;
      setProject(extracted.project || {});
      setComponents(extracted.components || []);
      setStep("review");
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Upload failed. Please try again.",
      );
    } finally {
      setUploading(false);
    }
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  // ── Project field changes ─────────────────────────────────
  const handleProjectChange = (e) =>
    setProject((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  // ── Component changes ─────────────────────────────────────
  const handleSpecChange = (idx, field, value) =>
    setComponents((prev) => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        specifications: { ...updated[idx].specifications, [field]: value },
      };
      return updated;
    });

  const handleTagChange = (idx, value) =>
    setComponents((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], tag_number: value };
      return updated;
    });

  const handleTypeChange = (idx, value) =>
    setComponents((prev) => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        component_type: value,
        specifications: {},
      };
      return updated;
    });

  const handleRemoveComponent = (idx) =>
    setComponents((prev) => prev.filter((_, i) => i !== idx));

  const handleAddComponent = () => {
    setComponents((prev) => [
      ...prev,
      { component_type: "TRANS", tag_number: "", specifications: {} },
    ]);
    setEditingComp(components.length);
  };

  // ── Spec fill count ───────────────────────────────────────
  const filledCount = (comp) =>
    Object.values(comp.specifications || {}).filter(Boolean).length;

  // ── Validate before save ──────────────────────────────────
  const canConfirm = Boolean(
    project?.project_number?.trim() &&
    project?.vessel_name?.trim() &&
    project?.customer_name?.trim(),
  );

  // ── Save project + components ─────────────────────────────
  const handleConfirm = async () => {
    if (!canConfirm) {
      setError("Project Number, Vessel Name and Customer Name are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const projRes = await api.post("/projects/", {
        ...project,
        status: "active",
      });
      const projectId = projRes.data.id;

      // Save components sequentially to preserve order
      for (const comp of components) {
        await api.post("/components/", {
          project_id: projectId,
          component_type: comp.component_type,
          tag_number: comp.tag_number?.trim() || null,
          // Strip empty strings
          specifications: Object.fromEntries(
            Object.entries(comp.specifications || {}).filter(
              ([, v]) => v !== "" && v !== null,
            ),
          ),
        });
      }
      navigate(`/project/${projectId}`);
    } catch (err) {
      const detail = (err?.response?.data?.detail || "").toLowerCase();
      if (detail.includes("already exists") || detail.includes("unique")) {
        setError(
          `Project number "${project.project_number}" already exists. Please use a different number.`,
        );
      } else if (!err?.response) {
        setError("Cannot reach the server. Please check your connection.");
      } else {
        setError(
          err?.response?.data?.detail ||
            "Failed to save project. Please try again.",
        );
      }
      setSaving(false);
    }
  };

  // ══════════════════════════════════════════════════════════
  // UPLOAD STEP
  // ══════════════════════════════════════════════════════════

  if (step === "upload") {
    return (
      <Box>
        <Typography
          variant="body2"
          color="text.secondary"
          onClick={() => navigate("/create")}
          sx={{
            mb: "0.5rem",
            cursor: "pointer",
            "&:hover": { color: PRIMARY },
          }}
        >
          ← Back
        </Typography>

        <Box sx={{ mb: "2rem" }}>
          <Typography variant="h4" sx={{ color: DARK, fontWeight: "bold" }}>
            Import from Document
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload a customer spec sheet or datasheet — AI will extract all the
            details
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            onClose={() => setError("")}
            sx={{ mb: "1rem", borderRadius: "8px" }}
          >
            {error}
          </Alert>
        )}

        {/* ── Drop zone ──────────────────────────────────── */}
        <Card
          elevation={0}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => !uploading && fileRef.current?.click()}
          sx={{
            borderRadius: "16px",
            border: `2px dashed ${dragOver ? PRIMARY : "#ccc"}`,
            backgroundColor: dragOver ? "#e6f4f4" : "white",
            padding: "4rem 2rem",
            textAlign: "center",
            cursor: uploading ? "default" : "pointer",
            transition: "all 0.2s ease",
            "&:hover": { borderColor: PRIMARY, backgroundColor: "#f0fafa" },
          }}
        >
          {uploading ? (
            <Box>
              <CircularProgress sx={{ color: PRIMARY, mb: "1rem" }} />
              <Typography variant="h6" color="text.secondary">
                Analysing document...
              </Typography>
              <LinearProgress
                sx={{
                  mt: "1rem",
                  maxWidth: 400,
                  margin: "1rem auto 0",
                  "& .MuiLinearProgress-bar": { backgroundColor: PRIMARY },
                }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: "0.5rem" }}
              >
                AI is extracting component specifications
              </Typography>
            </Box>
          ) : (
            <Box>
              <UploadFileIcon
                sx={{ fontSize: 72, color: PRIMARY, mb: "1rem" }}
              />
              <Typography
                variant="h5"
                sx={{ color: DARK, fontWeight: "bold", mb: "0.5rem" }}
              >
                Drop your file here
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: "1.5rem" }}
              >
                or click to browse — PDF and Word (.docx) supported
              </Typography>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: PRIMARY,
                  borderRadius: "8px",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#004749" },
                }}
              >
                Browse Files
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: "1rem" }}
              >
                {fileName || "No file selected"}
              </Typography>
            </Box>
          )}
        </Card>

        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx"
          style={{ display: "none" }}
          onChange={(e) => handleFile(e.target.files[0])}
        />

        <Box sx={{ display: "flex", gap: 1, mt: "1.5rem", flexWrap: "wrap" }}>
          {[
            "📄 PDF Spec Sheets",
            "📝 Word Datasheets",
            "📋 Requirements Documents",
            "🔧 Technical Enquiries",
          ].map((t) => (
            <Chip
              key={t}
              label={t}
              size="small"
              sx={{
                backgroundColor: "#e6f4f4",
                color: PRIMARY,
                fontSize: "0.75rem",
              }}
            />
          ))}
        </Box>
      </Box>
    );
  }

  // ══════════════════════════════════════════════════════════
  // REVIEW STEP
  // ══════════════════════════════════════════════════════════

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: "1.5rem",
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ color: DARK, fontWeight: "bold" }}>
            Review Extracted Data
          </Typography>
          <Typography variant="body2" color="text.secondary">
            ✅ AI extracted data from <strong>{fileName}</strong> — review and
            edit before confirming
          </Typography>
        </Box>
        <Chip
          icon={<CheckCircleIcon />}
          label={`${components.length} component${components.length !== 1 ? "s" : ""} found`}
          sx={{
            backgroundColor: "#e6f5ec",
            color: "#00843d",
            fontWeight: "bold",
          }}
        />
      </Box>

      {error && (
        <Alert
          severity="error"
          onClose={() => setError("")}
          sx={{ mb: "1rem", borderRadius: "8px" }}
        >
          {error}
        </Alert>
      )}

      {/* ── Project details ───────────────────────────────── */}
      <Card
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: `2px solid ${PRIMARY}`,
          mb: "2rem",
        }}
      >
        <Box
          sx={{
            backgroundColor: PRIMARY,
            color: "white",
            padding: "0.75rem 1.25rem",
            borderRadius: "11px 11px 0 0",
            fontWeight: "bold",
          }}
        >
          📋 Project Details — Edit if needed
        </Box>
        <CardContent>
          <Grid container spacing={2}>
            {[
              {
                label: "Project Number",
                name: "project_number",
                required: true,
              },
              { label: "Customer Name", name: "customer_name", required: true },
              { label: "Vessel Name", name: "vessel_name", required: true },
              { label: "Flag State", name: "flag_state", required: false },
              {
                label: "Voltage System",
                name: "voltage_system",
                required: false,
                placeholder: "e.g. 690V / 60Hz or 6.6kV / 60Hz",
              },
              { label: "Frequency (Hz)", name: "frequency", required: false },
            ].map(({ label, name, required, placeholder }) => (
              <Grid item xs={12} sm={6} key={name}>
                <TextField
                  fullWidth
                  label={label}
                  name={name}
                  required={required}
                  placeholder={placeholder || ""}
                  sx={fieldStyle}
                  value={project?.[name] || ""}
                  onChange={handleProjectChange}
                />
              </Grid>
            ))}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Vessel Type"
                name="vessel_type"
                sx={fieldStyle}
                value={project?.vessel_type || ""}
                onChange={handleProjectChange}
              >
                {VESSEL_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Classification Society"
                name="classification_society"
                sx={fieldStyle}
                value={project?.classification_society || ""}
                onChange={handleProjectChange}
              >
                {CLASSIFICATION_SOCIETIES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                name="notes"
                sx={fieldStyle}
                value={project?.notes || ""}
                onChange={handleProjectChange}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Components ───────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: "1rem",
        }}
      >
        <Typography variant="h5" sx={{ color: DARK, fontWeight: "bold" }}>
          Components ({components.length})
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={handleAddComponent}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            borderRadius: "8px",
            textTransform: "none",
            "&:hover": { backgroundColor: "#e6f4f4" },
          }}
        >
          + Add Component
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: "2rem" }}>
        {components.map((comp, idx) => {
          const color = COMPONENT_COLORS[comp.component_type] || {
            bg: PRIMARY,
            light: "#e6f4f4",
          };
          const isEditing = editingComp === idx;
          const specFields = COMPONENT_SPECS[comp.component_type] || [];
          const filled = filledCount(comp);
          const total = specFields.length;

          return (
            <Grid item xs={12} md={6} key={idx}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: "12px",
                  border: "1px solid #e0e0e0",
                  borderTop: `4px solid ${color.bg}`,
                }}
              >
                <CardContent>
                  {/* Card header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: "0.75rem",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          backgroundColor: color.bg,
                          color: "white",
                          borderRadius: "8px",
                          padding: "0.3rem 0.75rem",
                          fontWeight: "bold",
                          fontSize: "0.85rem",
                        }}
                      >
                        {comp.component_type}
                      </Box>
                      <Typography variant="body2" sx={{ color: "#555" }}>
                        {comp.tag_number || "No tag"}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={`${filled}/${total} fields`}
                        size="small"
                        sx={{
                          backgroundColor:
                            filled === 0
                              ? "#fce8ed"
                              : filled < total * 0.5
                                ? "#fff3cd"
                                : "#e6f5ec",
                          color:
                            filled === 0
                              ? "#b00033"
                              : filled < total * 0.5
                                ? "#856404"
                                : "#00843d",
                          fontWeight: "bold",
                          fontSize: "0.7rem",
                        }}
                      />
                      <Tooltip title={isEditing ? "Collapse" : "Edit"}>
                        <IconButton
                          size="small"
                          onClick={() => setEditingComp(isEditing ? null : idx)}
                          sx={{ color: PRIMARY }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveComponent(idx)}
                          sx={{ color: "#b00033" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Progress bar */}
                  <LinearProgress
                    variant="determinate"
                    value={total > 0 ? (filled / total) * 100 : 0}
                    sx={{
                      height: 4,
                      borderRadius: 2,
                      mb: "0.75rem",
                      backgroundColor: "#e0e0e0",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor:
                          filled === 0
                            ? "#b00033"
                            : filled < total * 0.5
                              ? "#c47a00"
                              : "#00843d",
                        borderRadius: 2,
                      },
                    }}
                  />

                  {/* Collapsed — spec preview */}
                  {!isEditing && (
                    <Box
                      sx={{
                        backgroundColor: "#f8f9fa",
                        borderRadius: "8px",
                        padding: "0.75rem",
                      }}
                    >
                      {Object.entries(comp.specifications || {})
                        .filter(([k, v]) => v && !isDimField(k))
                        .slice(0, 6)
                        .map(([k, v]) => (
                          <Typography
                            key={k}
                            variant="body2"
                            sx={{ color: "#555", mb: "0.15rem" }}
                          >
                            <strong>{formatLabel(k)}:</strong> {v}
                          </Typography>
                        ))}
                      {Object.entries(comp.specifications || {}).filter(
                        ([k, v]) => isDimField(k) && v,
                      ).length > 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#b45309",
                            fontWeight: "bold",
                            display: "block",
                            mt: "0.25rem",
                          }}
                        >
                          📐 Dimension limits set
                        </Typography>
                      )}
                      {Object.keys(comp.specifications || {}).length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          No specs extracted — click ✏️ Edit to add manually
                        </Typography>
                      )}
                      {Object.entries(comp.specifications || {}).filter(
                        ([k, v]) => v && !isDimField(k),
                      ).length > 6 && (
                        <Typography
                          variant="caption"
                          onClick={() => setEditingComp(idx)}
                          sx={{
                            color: PRIMARY,
                            cursor: "pointer",
                            fontWeight: "bold",
                          }}
                        >
                          +
                          {Object.entries(comp.specifications).filter(
                            ([k, v]) => v && !isDimField(k),
                          ).length - 6}{" "}
                          more — click to expand
                        </Typography>
                      )}
                    </Box>
                  )}

                  {/* Expanded — edit form */}
                  {isEditing && (
                    <Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            select
                            label="Component Type"
                            value={comp.component_type}
                            sx={fieldStyle}
                            onChange={(e) =>
                              handleTypeChange(idx, e.target.value)
                            }
                          >
                            {COMPONENT_TYPES.map((t) => (
                              <MenuItem key={t} value={t}>
                                {t}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Tag Number"
                            value={comp.tag_number || ""}
                            sx={fieldStyle}
                            onChange={(e) =>
                              handleTagChange(idx, e.target.value)
                            }
                          />
                        </Grid>

                        {/* Dimension notice */}
                        {specFields.some((f) => isDimField(f.key)) &&
                          specFields
                            .filter((f) => isDimField(f.key))
                            .some((f) => comp.specifications?.[f.key]) && (
                            <Grid item xs={12}>
                              <Box
                                sx={{
                                  backgroundColor: "#fff8e1",
                                  border: "1px solid #ffe082",
                                  borderRadius: "8px",
                                  padding: "0.6rem 1rem",
                                  mt: "0.5rem",
                                  mb: "0.25rem",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "bold", color: "#b45309" }}
                                >
                                  📐 Physical Envelope — Do Not Exceed
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#78716c" }}
                                >
                                  Leave blank if no dimensional restrictions
                                  apply.
                                </Typography>
                              </Box>
                            </Grid>
                          )}

                        {specFields.map((field) => (
                          <Grid
                            item
                            xs={12}
                            sm={field.type === "multiline" ? 12 : 6}
                            key={field.key}
                          >
                            <TextField
                              fullWidth
                              label={field.label}
                              placeholder={field.placeholder}
                              value={
                                comp.specifications?.[field.key] != null
                                  ? String(comp.specifications[field.key])
                                  : ""
                              }
                              multiline={field.type === "multiline"}
                              rows={field.type === "multiline" ? 3 : 1}
                              onChange={(e) =>
                                handleSpecChange(idx, field.key, e.target.value)
                              }
                              sx={{
                                ...fieldStyle,
                                ...(isDimField(field.key) && {
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "8px",
                                    backgroundColor: "#fffdf5",
                                    "&.Mui-focused fieldset": {
                                      borderColor: "#b45309",
                                    },
                                  },
                                  "& label.Mui-focused": { color: "#b45309" },
                                }),
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>

                      <Button
                        size="small"
                        startIcon={<SaveIcon />}
                        onClick={() => setEditingComp(null)}
                        sx={{
                          mt: "1rem",
                          color: PRIMARY,
                          fontWeight: "bold",
                          textTransform: "none",
                          "&:hover": { backgroundColor: color.light },
                        }}
                      >
                        Done Editing
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Divider sx={{ mb: "1.5rem" }} />

      {/* ── Action buttons ────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleConfirm}
          disabled={saving || !canConfirm}
          startIcon={
            saving ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              <CheckCircleIcon />
            )
          }
          sx={{
            backgroundColor: PRIMARY,
            borderRadius: "8px",
            padding: "0.75rem 2rem",
            fontWeight: "bold",
            textTransform: "none",
            "&:hover": { backgroundColor: "#004749" },
          }}
        >
          {saving ? "Creating Project..." : "✅ Confirm & Create Project"}
        </Button>
        <Button
          variant="outlined"
          size="large"
          onClick={() => {
            setStep("upload");
            setError("");
          }}
          sx={{
            borderColor: PRIMARY,
            color: PRIMARY,
            borderRadius: "8px",
            padding: "0.75rem 2rem",
            textTransform: "none",
            "&:hover": { backgroundColor: "#e6f4f4" },
          }}
        >
          ← Re-upload
        </Button>
      </Box>
    </Box>
  );
}

export default ImportProject;
